import { Component, OnInit, inject } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AgentAccessService, AgentAccessToken } from '../../services/agent-access.service';
import { AuthService } from '../../services/auth.service';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { AgentTokenDialogComponent } from '../dialogs/agent-token-dialog/agent-token-dialog.component';

/**
 * Einstellungen › Agent-Zugang: connect your own AI agent (Claude Code, Claude
 * Desktop, Cursor …) over MCP with a personal token. The agent acts with your
 * rights; changes wait for your approval in the Portal-Assistent panel.
 * Admins/managers can also see and revoke the organisation's tokens.
 */
@Component({
  selector: 'app-agent-access-panel',
  templateUrl: './agent-access-panel.component.html',
  styleUrls: ['./agent-access-panel.component.scss'],
})
export class AgentAccessPanelComponent implements OnInit {
  private readonly access = inject(AgentAccessService);
  private readonly dialog = inject(NbDialogService);
  private readonly toastr = inject(NbToastrService);
  private readonly translate = inject(TranslateService);

  readonly mcpUrl = this.access.mcpUrl;
  readonly canSeeAll: boolean;

  tokens: AgentAccessToken[] = [];
  loading = true;
  enabled = true;
  showAll = false;
  revoking: Record<string, boolean> = {};

  constructor(auth: AuthService) {
    const role = (auth.accessTokenPayload$.value?.role || '').toLowerCase();
    this.canSeeAll = ['admin', 'manager', 'super_admin'].includes(role);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.access
      .list(this.showAll)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.tokens = res.data ?? [];
          this.enabled = res.enabled !== false;
        },
        error: () => this.toastr.danger(this.translate.instant('pages.settings.agentAccess.errors.load'), ''),
      });
  }

  toggleAll(value: boolean): void {
    this.showAll = value;
    this.load();
  }

  connect(): void {
    this.dialog
      .open(AgentTokenDialogComponent, { closeOnBackdropClick: false, hasBackdrop: true })
      .onClose.subscribe((created) => {
        if (created) this.load();
      });
  }

  revoke(token: AgentAccessToken): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'pages.settings.agentAccess.revokeDialog.title',
          message: this.translate.instant('pages.settings.agentAccess.revokeDialog.message', { name: token.name }),
          confirmLabel: 'pages.settings.agentAccess.revokeDialog.confirm',
          cancelLabel: 'buttons.cancel',
          confirmStatus: 'primary',
          cancelStatus: 'basic',
        },
        hasBackdrop: true,
      })
      .onClose.subscribe((confirmed) => {
        if (!confirmed) return;
        this.revoking[token.id] = true;
        this.access
          .revoke(token.id)
          .pipe(finalize(() => (this.revoking[token.id] = false)))
          .subscribe({
            next: () => {
              this.tokens = this.tokens.filter((t) => t.id !== token.id);
              this.toastr.success(this.translate.instant('pages.settings.agentAccess.revoked'), '');
            },
            error: () => this.toastr.danger(this.translate.instant('pages.settings.agentAccess.errors.revoke'), ''),
          });
      });
  }

  trackToken(_: number, token: AgentAccessToken): string {
    return token.id;
  }
}
